/* ============================================================================
 * fm-consent-banner.js — keep the Consentmo cookie banner clear of the PDP
 * buy bar and options sheet.
 *
 * The Consentmo app injects `<csm-cookie-consent>` with an inline
 * `z-index: 2147483647` (the maximum), and the banner itself —
 * `.csm-cookie-box`, fixed to the bottom of the viewport — lives inside the
 * element's shadow root at z-index 99999999. Nothing in the theme can paint
 * above it, and no theme stylesheet can reach inside the shadow root, so the
 * banner sat on top of the mobile buy bar (burying the only "Select colour &
 * size" call to action on the page) and covered the bottom 40% of the open
 * options sheet, including its Add to Basket footer.
 *
 * The shadow root is open, so JS can restyle what CSS cannot. This script
 * appends one <style> into the shadow root and then only toggles attributes
 * on the host element:
 *
 *   data-fm-lift     — banner bottom becomes var(--fm-buybar-height), so it
 *                      stacks ABOVE the buy bar instead of on top of it.
 *                      (--fm-buybar-height is measured onto <html> by
 *                      fm-options-sheet.js / fm-buybar-mtm.js, and custom
 *                      properties inherit across the shadow boundary.)
 *                      Applied only when the buy bar is actually on screen:
 *                      mobile breakpoint + body.fm-sheet-active.
 *   data-fm-suppress — banner hidden while the options sheet
 *                      (body.fm-sheet-open), the legacy made-to-measure
 *                      modal (body.pdp-options-modal-open) or the logo mat
 *                      wizard modal (body.lmw-modal-open, added 23-Sep-2026
 *                      — it was covering the wizard's Next/Generate footer
 *                      for anyone who opened the form before dismissing
 *                      cookies) is up. It comes straight back when the
 *                      overlay closes, so consent is deferred, never denied
 *                      a prompt.
 *
 * Loaded by snippets/fm-options-sheet.liquid (standard PDPs),
 * snippets/fm-buybar-mtm.liquid (made-to-measure PDPs) and
 * sections/main-product.liquid on the eq-custom (logo mat) templates.
 * Everywhere else the banner is left alone. Loading it twice is harmless:
 * attach() wires each host once.
 *
 * If Consentmo ever closes its shadow root or renames .csm-cookie-box this
 * degrades to doing nothing — the banner overlaps again but nothing breaks.
 * ==========================================================================*/
(function () {
  'use strict';

  var HOST_TAG = 'csm-cookie-consent';
  var STYLE_ID = 'fm-consent-banner-fixups';

  /* Same breakpoint as fm-options-sheet.css — the buy bar only exists here. */
  var mq = window.matchMedia('(max-width: 767px)');

  function ensureStyle(host) {
    var root = host.shadowRoot;
    if (!root) return null;
    var existing = root.querySelector('#' + STYLE_ID);
    if (existing) return existing;
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent =
      ':host([data-fm-lift]) .csm-cookie-box{' +
        'bottom:var(--fm-buybar-height,96px) !important;' +
      '}' +
      ':host([data-fm-suppress]) .csm-cookie-box{' +
        'display:none !important;' +
      '}';
    root.appendChild(st);
    return st;
  }

  function update(host) {
    var body = document.body;
    var overlayOpen =
      body.classList.contains('fm-sheet-open') ||
      body.classList.contains('pdp-options-modal-open') ||
      body.classList.contains('lmw-modal-open');
    /* The buy bar hides while the inline "Choose your options" CTA is in
       view (fm-options-sheet.js toggles the class) — the banner drops back
       to the viewport bottom rather than floating above an empty gap. */
    var buybarOnScreen = mq.matches &&
      body.classList.contains('fm-sheet-active') &&
      !body.classList.contains('fm-inline-cta-onscreen');

    if (overlayOpen) host.setAttribute('data-fm-suppress', '');
    else host.removeAttribute('data-fm-suppress');

    if (buybarOnScreen) host.setAttribute('data-fm-lift', '');
    else host.removeAttribute('data-fm-lift');
  }

  function attach(host) {
    if (host.__fmConsentBannerWired) return;
    if (!ensureStyle(host)) return; /* closed shadow root — leave it alone */
    host.__fmConsentBannerWired = true;

    update(host);

    /* The sheet and the legacy modal both signal open/close purely through
       body classes, so watching those covers every controller without
       touching any of them. */
    var classWatch = new MutationObserver(function () { update(host); });
    classWatch.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    if (mq.addEventListener) mq.addEventListener('change', function () { update(host); });
    else if (mq.addListener) mq.addListener(function () { update(host); });

    /* Consentmo re-renders its shadow content on consent changes; if our
       <style> gets wiped in the process, put it back. */
    var rerenderWatch = new MutationObserver(function () { ensureStyle(host); });
    rerenderWatch.observe(host.shadowRoot, { childList: true });
  }

  function init() {
    var host = document.querySelector(HOST_TAG);
    if (host) { attach(host); return; }

    /* The app injects the element asynchronously — wait for it, then stop
       watching the moment it appears. */
    var appearWatch = new MutationObserver(function () {
      var found = document.querySelector(HOST_TAG);
      if (found) {
        appearWatch.disconnect();
        attach(found);
      }
    });
    appearWatch.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
